import { List, Map } from 'immutable';

import HomeClient from 'flow/api-clients/home-client';
import appConfig from 'app-config';

const POSITION_POSTFIXED = 'postfixed';
const BYTE_TO_GIGABYTE_MULTIPLIER = 1000 * 1000 * 1000;

const getRoamingCutOffPrice = (simcard) => {
    if (!simcard) {
        return 0;
    }

    return simcard.getIn(
        ['roamingData', 'roamingCutOffPriceLimit', 'price'],
        0,
    );
};

export const getRoamingCurrencySymbol = (simcard) => {
    if (!simcard) {
        return undefined;
    }

    return simcard.getIn([
        'roamingData',
        'roamingCutOffPriceLimit',
        'currency',
        'symbol',
    ]);
};

const getRoamingCurrencySymbolPosition = (simcard) => {
    if (!simcard) {
        return undefined;
    }

    return simcard.getIn([
        'roamingData',
        'roamingCutOffPriceLimit',
        'currency',
        'symbolPositionType',
    ]);
};

export const getRoamingCutOffPriceString = (simcard) => {
    if (!simcard) {
        return null;
    }

    const price = getRoamingCutOffPrice(simcard);
    const currencySymbol = getRoamingCurrencySymbol(simcard);
    const symbolPosition = getRoamingCurrencySymbolPosition(simcard);

    const cutOffPriceString =
        symbolPosition === POSITION_POSTFIXED
            ? price + currencySymbol
            : currencySymbol + price;
    return cutOffPriceString || null;
};

const getRoamingUsagePercentage = (simcard) => {
    if (!simcard) {
        return undefined;
    }

    return simcard.getIn(['roamingData', 'roamingUsagePercentage'], 0);
};

export const getRoamingUsagePercentageRounded = (simcard) => {
    if (!simcard) {
        return '-';
    }

    const usage = getRoamingUsagePercentage(simcard);
    return Math.round(100 * usage) / 100 || 0;
};

const getRoamingCurrentUsageInCurrency = (simcard) => {
    if (!simcard) {
        return undefined;
    }

    const price = getRoamingCutOffPrice(simcard);
    const usage = getRoamingUsagePercentage(simcard);

    //Two decimals
    return Math.round(100 * (price * (usage / 100))) / 100 || 0;
};

export const getRoamingCurrentUsageInCurrencyString = (simcard) => {
    if (!simcard) {
        return null;
    }

    const usage = getRoamingCurrentUsageInCurrency(simcard);
    const currencySymbol = getRoamingCurrencySymbol(simcard);
    const symbolPosition = getRoamingCurrencySymbolPosition(simcard);

    const usageInCurrency =
        symbolPosition === POSITION_POSTFIXED
            ? usage + currencySymbol
            : currencySymbol + usage;
    return (usage || usage === 0) && currencySymbol ? usageInCurrency : null;
};

const getDataUsedThisMonth = (simcard) => {
    if (!simcard) {
        return undefined;
    }

    return simcard.getIn(['usage', 'surfUsedThisMonth'], 0);
};

const getDataMaxThisMonth = (simcard) => {
    if (!simcard) {
        return undefined;
    }

    return simcard.getIn(['usage', 'surfPackageMaxThisMonth']);
};

export const getNumberOfTopUpsThisMonth = (mobileSubscription) => {
    if (!mobileSubscription) {
        return 0;
    }

    return mobileSubscription.get('topUpThisMonth') || 0;
};

const getTopUpDataAddedThisMonth = (mobileSubscription) => {
    if (!mobileSubscription) {
        return undefined;
    }

    const topUps = getNumberOfTopUpsThisMonth(mobileSubscription);

    //One topUp = 1 GB
    return topUps * BYTE_TO_GIGABYTE_MULTIPLIER;
};

const getDataMaxThisMonthMinusTopUps = (mobileSubscription, simcardKey) => {
    if (!mobileSubscription || !simcardKey) {
        return undefined;
    }

    const simcard = getSimcardWithKey(mobileSubscription, simcardKey);
    const dataMax = getDataMaxThisMonth(simcard);
    const topUpData = getTopUpDataAddedThisMonth(mobileSubscription);

    return dataMax - topUpData;
};

export const getDataUsedThisMonthInPercentUiFriendly = (simcard) => {
    if (!simcard) {
        return '-';
    }

    const dataUsed = getDataUsedThisMonth(simcard);
    const dataMax = getDataMaxThisMonth(simcard);

    return Math.round(10000 * (dataUsed / dataMax)) / 100 || '-';
};

export const getDataUsedThisMonthUiFriendly = (simcard) => {
    if (!simcard) {
        return undefined;
    }

    const dataUsed = getDataUsedThisMonth(simcard);
    return getDataInGbWithTwoDecimalsMaximum(dataUsed);
};

export const getDataMaxThisMonthUiFriendly = (simcard) => {
    if (!simcard) {
        return undefined;
    }

    const dataAvailable = getDataMaxThisMonth(simcard);
    return getDataInGbWithTwoDecimalsMaximum(dataAvailable);
};

export const getDataMaxThisMonthMinusTopUpsUiFriendly = (
    mobileSubscription,
    simcardKey,
) => {
    if (!mobileSubscription || !simcardKey) {
        return undefined;
    }

    const data = getDataMaxThisMonthMinusTopUps(mobileSubscription, simcardKey);
    return getDataInGbWithTwoDecimalsMaximum(data);
};

export const getTopUpDataAddedThisMonthUiFriendly = (mobileSubscription) => {
    if (!mobileSubscription) {
        return undefined;
    }

    const topUpData = getTopUpDataAddedThisMonth(mobileSubscription);
    return getDataInGbWithTwoDecimalsMaximum(topUpData);
};

const getSimcardWithKey = (mobileSubscription, simcardKey) => {
    if (!mobileSubscription || !simcardKey) {
        return Map();
    }

    const simcards = mobileSubscription.get('simcards', List());
    return simcards.find((sim) => sim.get('key') === simcardKey) || Map();
};

const getDataInGbWithTwoDecimalsMaximum = (value) => {
    if (!value || isNaN(value)) {
        return 0;
    }

    return Math.round((100 * value) / BYTE_TO_GIGABYTE_MULTIPLIER) / 100;
};

export const isUnlimitedSurf = (mainSimcard) => {
    return mainSimcard && mainSimcard.getIn(['usage', 'treatAsUnlimited']);
};

/**
 * The default Same-Site policies prevents us from accepting cookies from the
 * home.telavox.se domain.
 *
 * Replace instances of 'home.telavox.se' with where we're making requests to
 * be able to embed the topup page.
 */
export const getTopupLink = (mobileSubscriptionDTO) => {
    let topupLink = mobileSubscriptionDTO.get('topUpLink');
    const homeDomain = 'https://home.telavox.se';
    if (topupLink.startsWith(homeDomain)) {
        // Under certain circumstances, the apiHost is empty. Default to the
        // flowUrl then.
        topupLink = topupLink.replace(
            homeDomain,
            HomeClient.apiHost || appConfig.apiUrl,
        );
    }
    return topupLink + '&noAppButtons=true';
};
